import { createSlice } from '@reduxjs/toolkit';
import { clearUserLogged, logoutAction, setSessionExpired, switchIdpAccount } from 'features/common/commonSlice';
import {
  getReleaseNotesAsync,
  getReleaseNotesUnreadCountAsync,
  markAllReleaseNotesReadAsync,
  markReleaseNoteReadAsync,
} from './asyncActions';
import { ReleaseNote } from './constants';

export interface ReleaseNotesState {
  notes: ReleaseNote[];
  /** false = aún sin pedir la lista. */
  loaded: boolean;
  loading: boolean;
  unreadCount: number;
}

const initialState: ReleaseNotesState = {
  notes: [],
  loaded: false,
  loading: false,
  unreadCount: 0,
};

const countUnread = (notes: ReleaseNote[]) => notes.filter((note) => !note.read).length;

export const releaseNotesSlice = createSlice({
  name: 'releaseNotes',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getReleaseNotesUnreadCountAsync.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(getReleaseNotesAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReleaseNotesAsync.fulfilled, (state, action) => {
        state.notes = action.payload;
        state.loaded = true;
        state.loading = false;
        state.unreadCount = countUnread(action.payload);
      })
      .addCase(getReleaseNotesAsync.rejected, (state) => {
        state.loading = false;
      })
      // Se marca en local en cuanto se envía: el indicador baja sin esperar a la red. Si el POST falla, el
      // siguiente refresco del contador lo vuelve a subir.
      .addCase(markReleaseNoteReadAsync.pending, (state, action) => {
        const note = state.notes.find((item) => item.id === action.meta.arg);
        if (note && !note.read) {
          note.read = true;
          note.readAt = Date.now();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllReleaseNotesReadAsync.pending, (state) => {
        const now = Date.now();
        state.notes.forEach((note) => {
          if (!note.read) {
            note.read = true;
            note.readAt = now;
          }
        });
        state.unreadCount = 0;
      })
      // El logout no recarga la página: sin esto el siguiente usuario vería el contador del anterior.
      .addCase(logoutAction, () => initialState)
      .addCase(clearUserLogged, () => initialState)
      .addCase(setSessionExpired, () => initialState)
      .addCase(switchIdpAccount, () => initialState);
  },
});

export default releaseNotesSlice.reducer;
